import styled, {keyframes} from 'styled-components';
import type {PlushState} from './PaiPlush';
import {KnitBeanie, PlushFace, PlushFrame} from './plushKit';

/**
 * Penny: a navy penguin plush holding up a gold coin in one flipper. When
 * `happy` she flips it — the coin spins up above her flipper and lands back
 * in it. White heart-shaped face and belly, a small orange beak, orange
 * feet.
 */

const FUR = {light: '#5E7393', mid: '#3F5170', dark: '#2A3652'};
const FLIPPER = '#33425E';
const WHITE = '#F7F4EE';
const BEAK = '#F4A340';
const COIN = '#E8B53A';
const COIN_EDGE = '#B8862A';

/** Where the coin rests in the raised flipper, in viewBox units. */
const COIN_REST = {x: 172, y: 100};

const flip = keyframes`
  0%, 100% { transform: translateY(0) scaleX(1); }
  10% { transform: translateY(-10px) scaleX(0.2); }
  20% { transform: translateY(-30px) scaleX(1); }
  30% { transform: translateY(-44px) scaleX(0.2); }
  40% { transform: translateY(-48px) scaleX(1); }
  50% { transform: translateY(-40px) scaleX(0.2); }
  60% { transform: translateY(-20px) scaleX(1); }
  70% { transform: translateY(0) scaleX(0.4); }
  80% { transform: translateY(0) scaleX(1); }
`;

const Coin = styled.g<{$isFlipping: boolean}>`
  transform-origin: ${COIN_REST.x}px ${COIN_REST.y}px;
  animation: ${({$isFlipping}) => ($isFlipping ? flip : 'none')} 1.4s
    ease-in-out infinite;
`;

/** Props for {@link PennyPlush}. */
export interface PennyPlushProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
  /** Puts Beanie's knit hat on her, in the given color. Omitted means no hat. */
  beanieColor?: string;
}

/**
 * Renders Penny (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link PennyPlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function PennyPlush({
  size,
  state = 'idle',
  beanieColor,
}: PennyPlushProps) {
  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={FUR}
      eyeLineFraction={100 / 220}
      thoughtAnchor={{x: 140, y: 40}}
    >
      {kit => (
        <>
          <g filter={kit.fuzz}>
            <ellipse cx={78} cy={199} rx={19} ry={8} fill={BEAK} />
            <ellipse cx={122} cy={199} rx={19} ry={8} fill={BEAK} />
            <ellipse
              cx={28}
              cy={140}
              rx={12}
              ry={28}
              fill={FLIPPER}
              transform="rotate(18 28 140)"
            />
            <path
              d="M100 30 C150 30 176 72 176 122 C176 166 146 194 100 194 C54 194 24 166 24 122 C24 72 50 30 100 30 Z"
              fill={kit.fur}
            />
            {/* Raised flipper holding the coin, drawn over the body's edge. */}
            <ellipse
              cx={160}
              cy={124}
              rx={11}
              ry={26}
              fill={FLIPPER}
              transform="rotate(-32 160 124)"
            />
          </g>
          {/* Heart-shaped white face and belly. */}
          <g filter={kit.soft}>
            <ellipse cx={78} cy={104} rx={28} ry={28} fill={WHITE} />
            <ellipse cx={122} cy={104} rx={28} ry={28} fill={WHITE} />
            <ellipse cx={100} cy={152} rx={50} ry={40} fill={WHITE} />
          </g>
          {beanieColor !== undefined && (
            <KnitBeanie kit={kit} color={beanieColor} />
          )}
          <PlushFace kit={kit} x={100} y={100} scale={0.9} hasMouth={false} />
          <path d="M 91 112 Q 100 106 109 112 Q 100 124 91 112 Z" fill={BEAK} />
          <Coin $isFlipping={state === 'happy'}>
            <circle
              cx={COIN_REST.x}
              cy={COIN_REST.y}
              r={16}
              fill={COIN}
              stroke={COIN_EDGE}
              strokeWidth={3}
            />
            <circle
              cx={COIN_REST.x}
              cy={COIN_REST.y}
              r={10}
              fill="none"
              stroke={COIN_EDGE}
              strokeWidth={1.5}
              opacity={0.7}
            />
            <path
              d={`M ${COIN_REST.x} ${COIN_REST.y - 6} l 1.8 3.8 4.2 0.6 -3 2.9 0.7 4.2 -3.7 -2 -3.7 2 0.7 -4.2 -3 -2.9 4.2 -0.6 Z`}
              fill="#FFF1C4"
            />
          </Coin>
        </>
      )}
    </PlushFrame>
  );
}
